import { useEffect, useMemo, useRef, useState } from 'react'

import { useEliteBuyCtaExtras } from '../EliteBuyCtaExtrasContext'
import { useInView } from '../../../hooks/useInView'
import Button from '../../ui-components/Button'
import RenderHTML from '../../ui-components/RenderHTML'
import { convertEpochToDate, roundOffAndFormatNumberWithCurrency } from '../../../utils/utils'
import RiveComponent, { RiveFit } from '../../RiveComponent'
import { ELITE_RIVE_URL } from '../../../constants'
import { getTopPadding } from '../../../utils/sessionStorage'
import { useSetAppBarTheme } from '../../../hooks/useSetAppBarTheme'
import { EliteRenewal, EventNames } from '../../../types/misc.types'
import { postCTA, trackEvent } from '../../../utils/appBridge'

const activeCircle = `<svg xmlns="http://www.w3.org/2000/svg" width="4" height="4" viewBox="0 0 4 4" fill="none">
  <circle cx="2" cy="2" r="2" fill="#60F0B6"/>
</svg>`

const ShineIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="176" height="75" viewBox="0 0 176 75" fill="none">
  <g opacity="0.7" filter="url(#filter0_f_39936_7724)">
    <path d="M103.094 -8.44769H164.918L72.9579 123.512L11.1345 123.512L103.094 -8.44769Z" fill="white"/>
  </g>
  <defs>
    <filter id="filter0_f_39936_7724" x="0.134521" y="-19.4478" width="175.783" height="153.96" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
      <feFlood flood-opacity="0" result="BackgroundImageFix"/>
      <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
      <feGaussianBlur stdDeviation="5.5" result="effect1_foregroundBlur_39936_7724"/>
    </filter>
  </defs>
</svg>`

export const ELiteDashboardHead = ({
  handleEliteButtonVisibility,
  membershipValidity,
  renewal,
  onBGRiveLoad,
  onViewAllBenefitsClick
}: {
  handleEliteButtonVisibility: (x: boolean) => void
  membershipValidity: number
  renewal?: EliteRenewal
  onBGRiveLoad: () => void
  onViewAllBenefitsClick: () => void
}) => {
  const eliteButtonRef = useRef(null)

  const isEliteButtonInView = useInView(eliteButtonRef)

  const getAppBarTheme = () => {
    if (isEliteButtonInView === -1) {
      return null
    }
    return isEliteButtonInView ? 'light' : 'dark'
  }

  useSetAppBarTheme({ appBarTheme: getAppBarTheme() })

  const riveRatio = 1.74
  const [outsideRiveWidth, setOutsideRiveWidth] = useState(0)

  useEffect(() => {
    handleEliteButtonVisibility(!isEliteButtonInView)
  }, [isEliteButtonInView])

  const bgRiveheight = useMemo(() => {
    return outsideRiveWidth * (riveRatio ?? 1) || 700
  }, [outsideRiveWidth])

  return (
    <div className='flex flex-col bg-[#010101] relative z-0 pb-10 -mt-16'>
      <div
        ref={(ref) => {
          if (ref && outsideRiveWidth === 0) {
            setOutsideRiveWidth(ref?.clientWidth ?? 300)
          }
        }}
        style={{ height: bgRiveheight }}
        className='absolute w-full'
      >
        <RiveComponent
          src={`${ELITE_RIVE_URL}/post_elite_bg.riv`}
          onLoad={onBGRiveLoad}
          fit={RiveFit.WIDTH}
          stateMachines={'State Machine 1'}
        />
      </div>
      <div className='z-10 flex flex-col justify-center items-center w-full pt-[64px]'>
        <div
          style={{ paddingTop: `calc(40px + ${getTopPadding()}px)` }}
          // style={{ paddingTop: `calc(${outsideRiveWidth > 400 ? bgRiveheight * 0.1 : 56}px)` }}
          className='h-auto w-full flex flex-col items-center justify-center'
        >
          <div className='h-20 w-20 ml-[6px]'>
            <img src={'/images/elite/elite_badge_new.png'} alt='' aria-hidden='true' className='h-full w-auto' />
          </div>
          <h2 className='w-[150px] min-h-[31px] h-max pt-3 m-0'>
            <img src={'/images/elite/elite_and_name.svg'} alt='Powerup Elite Logo' />
          </h2>
        </div>

        <div className='px-5 pt-16 pb-4 w-full'>
          <div className='px-4 h-[50px] bg-[#232323] flex justify-between items-center w-full rounded-xl  text-[14px] '>
            <div className='text-white font-[400] opacity-80'>
              {membershipValidity ? `Valid till ${convertEpochToDate(membershipValidity)}` : 'Membership validity'}
            </div>

            <div className='flex gap-[6px] items-center'>
              <RenderHTML innerHTMLText={activeCircle} />
              <div className='text-[#60F0B6] font-[500]'>Active</div>
            </div>
          </div>
        </div>
        {renewal ? (
          <div ref={eliteButtonRef} className='pt-1 pb-2 w-full px-5 h-max'>
            <EliteRenewalCta renewal={renewal} />
          </div>
        ) : <div ref={eliteButtonRef} className='pt-10 pb-2 w-full px-5 h-max'>
          <Button
            onClick={() => {
              onViewAllBenefitsClick()
            }}
            aria-label='View all benefits'
            aria-description='Double tap to view all available benefits'
            buttonClassName={`!min-h-[50px] min-w-full mt-[-44px] font-[500] z-10 text-[14px] rounded-[12px] !bg-[#FFF2DF] `}
          >
            <div className='flex gap-2 items-center justify-center' aria-hidden='true'>
              <div className={`pb-[2px] !text-[#722200]`}>View all benefits</div>
            </div>
          </Button>
        </div>}
      </div>
    </div>
  )
}

export const EliteRenewalCta = ({ renewal }: { renewal: EliteRenewal }) => {
  const [shouldAnimateShine, setShouldAnimateShine] = useState(false)
  const eliteBuyCtaExtras = useEliteBuyCtaExtras()
  const isRenewCtaDisabled = !renewal?.cta

  useEffect(() => {
    if (isRenewCtaDisabled) return
    const timeoutId = setTimeout(() => {
      setShouldAnimateShine(true)
    }, 500)
    return () => clearTimeout(timeoutId)
  }, [isRenewCtaDisabled])

  return (
    <div className='relative flex flex-col w-full'>
      <div
        className={`relative w-full overflow-hidden rounded-[12px] px-4 pt-2 pb-[54px] text-center z-0 ${
          isRenewCtaDisabled ? 'bg-[#E8E0DC]' : 'bg-[#CC5E2F]'
        }`}
      >
        {isRenewCtaDisabled ? null : (
          <div
            className='absolute top-0 left-7 w-full h-full scale-125 pointer-events-none'
            style={{
              zIndex: 1,
              animation: shouldAnimateShine ? 'shine 1.2s ease-out forwards' : 'none',
              transform: 'translateX(-100%) scale(1.25)',
              opacity: 1
            }}
          >
            <RenderHTML innerHTMLText={ShineIcon} />
          </div>
        )}
        <div
          className={`relative text-[11px] leading-[150%] font-[400] tracking-[1px] uppercase ${
            isRenewCtaDisabled ? 'text-[#8E9393]' : 'text-white/80'
          }`}
        >
          Exclusively for you
        </div>
        <div
          className={`relative text-[13px] leading-[150%] font-[500] ${
            isRenewCtaDisabled ? 'text-[#8E9393]' : 'text-white'
          }`}
        >
          Renew now to{' '}
          <span className='font-[700]'>lock in your current price</span>
        </div>
      </div>
      <div className='relative z-30'>
        <Button
          disabled={isRenewCtaDisabled}
          onClick={() => {
            if (isRenewCtaDisabled) return
            trackEvent({
              eventName: EventNames.ButtonTap,
              payload: {
                ...eliteBuyCtaExtras,
                eventId: 'Renew',
                buttonLabel: 'Renew Elite',
                screenType: 'MF_Elite_Dashboard'
              }
            })
            if (renewal?.cta) {
              postCTA(JSON.stringify(renewal?.cta))
            }
          }}
          buttonClassName={`!min-h-[50px] min-w-full mt-[-44px] font-[500] !z-30 text-[14px] rounded-[12px] ${
            isRenewCtaDisabled ? '!bg-[#CDC5C2] cursor-not-allowed' : '!bg-[#FFFFFF]'
          }`}
        >
          <div className='flex gap-2 items-center justify-center'>
            <div
              className={`pb-[2px] text-[14px] font-[500] ${
                isRenewCtaDisabled ? '!text-[#8E9393]' : '!text-[#65321D]'
              }`}
            >
              Renew Elite @ {roundOffAndFormatNumberWithCurrency(renewal?.membershipFees?.amount ?? 0)}/yr
              <span className='text-[11px] font-[400] opacity-50 -mt-1'>+GST </span>
            </div>
          </div>
        </Button>
      </div>
      <style
        dangerouslySetInnerHTML={{
          __html: `
            @keyframes shine {
              0% {
                transform: translateX(-100%) scale(1.25);
                opacity: 1;
              }
              40% {
                transform: translateX(-20%) scale(1.25);
                opacity: 1;
              }
              100% {
                transform: translateX(100%) scale(1.25);
                opacity: 0;
              }
            }
          `
        }}
      />
    </div>
  )
}
